/**
 * Client della dashboard redazionale (decisioni 73-85).
 * ---------------------------------------------------------------------------
 * Passa tutto da `@/api/client`: nessun `fetch` da qui, nessun `fetch` dai
 * componenti. La dashboard parla SOLO con l'API vera — nessun ripiego sulle
 * fixture, che sono un surrogato di lettura del portale pubblico e non
 * saprebbero rispondere a una scrittura.
 *
 * Tre regole del contratto (§21) che vivono in questo file e in nessun altro:
 *
 *  · **`If-Match` su ogni scrittura di dettaglio.** L'`ETag` letto sulla GET
 *    torna indietro sulla PATCH: senza, il salvataggio sovrascrive in
 *    silenzio il lavoro di un altro. È qui, non nei componenti, perché
 *    dimenticarlo in una schermata su venti basta a perdere del lavoro.
 *  · **Il salvataggio automatico va su `PUT …/bozza/`, non su `PATCH`.** È
 *    quello che accorpa l'audit: una voce per sessione di lavoro invece di
 *    una ogni trenta secondi.
 *  · **Un solo spacchettatore di lista**: `{meta, risultati}`, senza
 *    `meta.attribuzione` — che nel namespace redazionale non c'è.
 */

import {
  apiDelete,
  apiGet,
  apiGetConEtag,
  apiOptions,
  apiPatchConEtag,
  apiPost,
  apiPostConEtag,
  apiPut,
  apiPutConEtag,
} from '@/api/client';
import type { QueryValue, RispostaConEtag } from '@/api/client';
import type {
  AnteprimaImportPoi,
  BloccoModifica,
  CodaLavoro,
  CompletezzaHome,
  ConfigurazioneImportatore,
  CriteriGeneratore,
  DescrittoreCampo,
  DescrizioneCampo,
  EsecuzioneImport,
  EsitoRiordino,
  LegameTappa,
  EsitoBatchImportPoi,
  EsitoImportPoi,
  EsitoBulk,
  FasciaHome,
  ImportazioneCsv,
  ListaMedia,
  ListaRisorsa,
  Media,
  MetaLista,
  Notifica,
  NumeriHome,
  PreferenzaColonne,
  RigaRisorsa,
  RispostaCatalogo,
  RispostaGenerazione,
  RispostaGeocodifica,
  RispostaNavigazione,
  RispostaRicalcolo,
  RispostaSalvataggioGeneratore,
  RisorsaCatalogo,
  SchemaStruttura,
  VoceSequenzaGeneratore,
  SorgenteImport,
  TraduzioneContenuto,
  TipoWidget,
  UtenteDashboard,
  VistaSalvata,
  VoceAudit,
  EsitoUnificazione,
  OrigineScelte,
  Vocabolario,
  VoceIndiceVocabolari,
  VoceMenuPubblica,
  VoceVocabolario,
} from './tipi';

const AUTH = { authenticated: true } as const;

/** Filtri di lista: chiavi libere (i `campi_filtro` cambiano per risorsa). */
export type FiltriLista = Readonly<Record<string, QueryValue>>;

// ---------------------------------------------------------------------------
// Catalogo — da qui parte tutto
// ---------------------------------------------------------------------------

export function getCatalogo(signal?: AbortSignal): Promise<RispostaCatalogo> {
  return apiGet<RispostaCatalogo>('/redazione/risorse/', { ...AUTH, signal });
}

// ---------------------------------------------------------------------------
// Liste e dettagli delle risorse generiche
// ---------------------------------------------------------------------------

export function getLista(
  entita: string,
  filtri: FiltriLista = {},
  signal?: AbortSignal,
): Promise<ListaRisorsa> {
  return apiGet<ListaRisorsa>(`/redazione/${entita}/`, {
    ...AUTH,
    signal,
    query: filtri,
  });
}

export function getCestino(
  entita: string,
  filtri: FiltriLista = {},
  signal?: AbortSignal,
): Promise<ListaRisorsa> {
  return apiGet<ListaRisorsa>(`/redazione/${entita}/cestino/`, {
    ...AUTH,
    signal,
    query: filtri,
  });
}

/** Dettaglio + `ETag`: l'ETag va conservato e rimandato con `If-Match`. */
export function getDettaglio(
  entita: string,
  id: string,
  signal?: AbortSignal,
): Promise<RispostaConEtag<RigaRisorsa>> {
  return apiGetConEtag<RigaRisorsa>(`/redazione/${entita}/${id}/`, { ...AUTH, signal });
}

export function creaOggetto(
  entita: string,
  dati: Readonly<Record<string, unknown>>,
): Promise<RispostaConEtag<RigaRisorsa>> {
  return apiPostConEtag<RigaRisorsa>(`/redazione/${entita}/`, dati, AUTH);
}

/**
 * Salvataggio esplicito. `etag` assente = si scrive senza `If-Match`: il
 * contratto lo ammette («senza `If-Match` la scrittura passa»), ma la
 * dashboard lo manda sempre quando ce l'ha.
 */
export function aggiornaOggetto(
  entita: string,
  id: string,
  dati: Readonly<Record<string, unknown>>,
  etag: string | null,
): Promise<RispostaConEtag<RigaRisorsa>> {
  return apiPatchConEtag<RigaRisorsa>(`/redazione/${entita}/${id}/`, dati, {
    ...AUTH,
    ...(etag ? { headers: { 'If-Match': etag } } : {}),
  });
}

/**
 * Salvataggio AUTOMATICO della bozza. Idempotente, non cambia stato, e
 * accorpa l'audit — per questo non è una `PATCH` (contratto §21.4).
 */
export function salvaBozza(
  entita: string,
  id: string,
  dati: Readonly<Record<string, unknown>>,
  etag: string | null,
): Promise<RispostaConEtag<RigaRisorsa>> {
  return apiPutConEtag<RigaRisorsa>(`/redazione/${entita}/${id}/bozza/`, dati, {
    ...AUTH,
    ...(etag ? { headers: { 'If-Match': etag } } : {}),
  });
}

/** `DELETE` = cestino dove la risorsa ha il soft-delete, altrimenti è finita. */
export function eliminaOggetto(entita: string, id: string): Promise<void> {
  return apiDelete(`/redazione/${entita}/${id}/`, AUTH);
}

export function ripristinaOggetto(entita: string, id: string): Promise<RigaRisorsa> {
  return apiPost<RigaRisorsa>(`/redazione/${entita}/${id}/ripristina/`, {}, AUTH);
}

/** Solo admin: è l'unica operazione della dashboard che non si annulla. */
export function eliminaDefinitivamente(entita: string, id: string): Promise<void> {
  return apiDelete(`/redazione/${entita}/${id}/definitivo/`, AUTH);
}

export function duplicaOggetto(entita: string, id: string): Promise<RigaRisorsa> {
  return apiPost<RigaRisorsa>(`/redazione/${entita}/${id}/duplica/`, {}, AUTH);
}

/**
 * Transizione di stato. Il grafo NON si ricalcola lato client: si passa uno
 * degli `stati_raggiungibili` che la riga porta con sé.
 */
export function cambiaStato(
  entita: string,
  id: string,
  stato: string,
  nota = '',
): Promise<RigaRisorsa> {
  return apiPost<RigaRisorsa>(`/redazione/${entita}/${id}/stato/`, { stato, nota }, AUTH);
}

// -- sequenza delle tappe di un itinerario (contratto §I.8) ------------------

/**
 * La sequenza ordinata dei legami di un itinerario.
 *
 * È un endpoint DEDICATO e non la lista generica filtrata: porta il titolo
 * della tappa già risolto, il tipo di luogo e lo stato redazionale, cioè
 * tutto quello che serve a costruire il riordino senza una richiesta per
 * riga per sapere che cosa si sta spostando.
 */
export function getSequenzaTappe(
  slugItinerario: string,
  signal?: AbortSignal,
): Promise<readonly LegameTappa[]> {
  return apiGet<readonly LegameTappa[]>(
    `/redazione/itinerari/${encodeURIComponent(slugItinerario)}/tappe/`,
    { ...AUTH, signal },
  );
}

/**
 * Riordino ATOMICO: **una chiamata con la sequenza completa**, non due PATCH
 * che si scambiano il numero.
 *
 * Perché la sequenza è completa e non un delta: un delta non permette al
 * server di verificare che il client stesse guardando la stessa lista. Se nel
 * frattempo qualcuno ha aggiunto una tappa, un delta la lascerebbe fuori in
 * silenzio; la sequenza completa prende 400 con `mancanti`, e chi riordina
 * scopre che la sua lista era vecchia.
 *
 * La risposta porta `tratte_azzerate`: gli id dei legami la cui tappa
 * precedente è cambiata e i cui `distanza_da_precedente_km` e
 * `tempo_da_precedente_minuti` sono tornati a `null`. **Vanno segnalati**:
 * tenerli sarebbe stato mostrare la misura di un tragitto che non esiste più,
 * ricalcolarli sarebbe stato inventarne uno — e non dirlo lascerebbe il
 * profilo dell'itinerario svuotarsi senza che nessuno se ne accorga.
 */
export function riordinaTappe(
  slugItinerario: string,
  ordine: readonly number[],
): Promise<EsitoRiordino> {
  return apiPost<EsitoRiordino>(
    `/redazione/itinerari/${encodeURIComponent(slugItinerario)}/tappe/riordina/`,
    { ordine },
    AUTH,
  );
}

// -- configuratore automatico di itinerari (contratto §G) --------------------

/**
 * `POST /redazione/itinerari/genera/` (decisioni 221-225).
 *
 * Namespace `/redazione/`, risposta PIATTA (niente busta `{meta, …}`).
 * Riservato a revisori e amministratori: il redattore comunale riceve 403 —
 * ed è per questo che la voce di menu e il pulsante spariscono per lui,
 * invece di promettere una schermata che il backend nega.
 */
export function generaItinerario(
  criteri: CriteriGeneratore,
  signal?: AbortSignal,
): Promise<RispostaGenerazione> {
  return apiPost<RispostaGenerazione>('/redazione/itinerari/genera/', criteri, {
    ...AUTH,
    ...(signal ? { signal } : {}),
  });
}

/**
 * `POST /redazione/itinerari/genera/ricalcola/` (decisione 226).
 *
 * `criteri` sono gli ORIGINALI del wizard, non quelli rimandati da `genera/`:
 * la risposta porta la partenza già risolta in coordinate e senza `tipo`, e
 * come input è un 400 (verificato dal vivo l'11/08/2026).
 *
 * La `proposta_rimozioni` della risposta NON è mai stata applicata dal
 * server: la sequenza torna intera e la conferma spetta a chi guarda.
 */
export function ricalcolaItinerarioGenerato(
  criteri: CriteriGeneratore,
  sequenza: readonly VoceSequenzaGeneratore[],
  signal?: AbortSignal,
): Promise<RispostaRicalcolo> {
  return apiPost<RispostaRicalcolo>(
    '/redazione/itinerari/genera/ricalcola/',
    { criteri, sequenza },
    { ...AUTH, ...(signal ? { signal } : {}) },
  );
}

/**
 * `POST /redazione/itinerari/genera/salva/` (decisione 227): crea un
 * itinerario NORMALE in bozza, nell'ordine ricevuto. Il titolo è SEMPRE
 * scritto a mano. 201 con slug: da lì si prosegue sull'editor normale.
 */
export function salvaItinerarioGenerato(
  titolo: string,
  criteri: CriteriGeneratore,
  sequenza: readonly VoceSequenzaGeneratore[],
): Promise<RispostaSalvataggioGeneratore> {
  return apiPost<RispostaSalvataggioGeneratore>(
    '/redazione/itinerari/genera/salva/',
    { titolo, criteri, sequenza },
    AUTH,
  );
}

/*
 * NOTA (§I.14, decisione 195): qui c'era `getDurataItinerario`, che leggeva
 * la durata calcolata dall'API PUBBLICA perché il serializer redazionale non
 * la esponeva — e su una bozza rispondeva 404 proprio mentre il confronto
 * serviva. Ora `GET /redazione/itinerari/{slug}/` porta `durata` completa
 * (sola lettura, stesso componente OpenAPI del pubblico) anche in bozza:
 * il pannello la legge dalla risposta redazionale e la funzione è sparita.
 */

// -- traduzioni --------------------------------------------------------------

export function getTraduzioneContenuto(
  entita: string,
  id: string,
  lingua: string,
  signal?: AbortSignal,
): Promise<TraduzioneContenuto> {
  return apiGet<TraduzioneContenuto>(`/redazione/${entita}/${id}/traduzioni/${lingua}/`, {
    ...AUTH,
    signal,
  });
}

/**
 * L'italiano NON è una traduzione: si modifica con `PATCH` sulla risorsa, e
 * `PUT …/traduzioni/it/` risponde 400. Le chiavi ammesse sono solo i
 * `campi_traducibili`: una chiave fuori elenco è un errore, non viene
 * ignorata — un refuso che sparisce in silenzio è una traduzione persa.
 */
export function salvaTraduzioneContenuto(
  entita: string,
  id: string,
  lingua: string,
  corpo: {
    readonly campi: Readonly<Record<string, unknown>>;
    readonly stato?: string;
    readonly aggiornato_il?: string | null;
  },
): Promise<TraduzioneContenuto> {
  return apiPut<TraduzioneContenuto>(
    `/redazione/${entita}/${id}/traduzioni/${lingua}/`,
    corpo,
    AUTH,
  );
}

// -- audit e lock ------------------------------------------------------------

export function getAuditOggetto(
  entita: string,
  id: string,
  signal?: AbortSignal,
): Promise<{ meta?: MetaLista; risultati?: readonly VoceAudit[] } | readonly VoceAudit[]> {
  return apiGet(`/redazione/${entita}/${id}/audit/`, { ...AUTH, signal });
}

export function prendiLock(entita: string, id: string): Promise<BloccoModifica> {
  return apiPost<BloccoModifica>(`/redazione/${entita}/${id}/blocco/`, {}, AUTH);
}

export function rilasciaLock(entita: string, id: string): Promise<void> {
  return apiDelete(`/redazione/${entita}/${id}/blocco/`, AUTH);
}

// -- azioni massive ----------------------------------------------------------

export function bulkStato(
  entita: string,
  ids: readonly number[],
  stato: string,
  nota = '',
): Promise<EsitoBulk> {
  return apiPost<EsitoBulk>(`/redazione/${entita}/bulk/stato/`, { ids, stato, nota }, AUTH);
}

export function bulkElimina(
  entita: string,
  ids: readonly number[],
  nota = '',
): Promise<EsitoBulk> {
  return apiPost<EsitoBulk>(`/redazione/${entita}/bulk/elimina/`, { ids, nota }, AUTH);
}

export function bulkRipristina(entita: string, ids: readonly number[]): Promise<EsitoBulk> {
  return apiPost<EsitoBulk>(`/redazione/${entita}/bulk/ripristina/`, { ids }, AUTH);
}

/** `comune` e `comuni` sono RIFIUTATI qui: per quelli c'è `bulkSposta`. */
export function bulkCampo(
  entita: string,
  ids: readonly number[],
  campo: string,
  valore: string | null,
): Promise<EsitoBulk> {
  return apiPost<EsitoBulk>(`/redazione/${entita}/bulk/campo/`, { ids, campo, valore }, AUTH);
}

export function bulkSposta(
  entita: string,
  ids: readonly number[],
  comune: string,
  duplica: boolean,
): Promise<EsitoBulk> {
  return apiPost<EsitoBulk>(
    `/redazione/${entita}/bulk/sposta/`,
    { ids, comune, duplica },
    AUTH,
  );
}

/**
 * URL dell'export. È un GET del browser (scarica un file), non una fetch:
 * gli stessi filtri e lo stesso ambito della lista — non si esporta più di
 * quanto si possa vedere.
 */
export function urlExport(
  entita: string,
  filtri: FiltriLista,
  formato: 'csv' | 'json',
): string {
  const base = (import.meta.env.VITE_API_BASE ?? '/api/v2').replace(/\/$/, '');
  const url = new URL(`${base}/redazione/${entita}/export/`, window.location.origin);
  url.searchParams.set('formato', formato);
  for (const [chiave, valore] of Object.entries(filtri)) {
    if (valore === undefined || valore === null || valore === '') continue;
    url.searchParams.set(chiave, String(valore));
  }
  return url.pathname + url.search;
}

// ---------------------------------------------------------------------------
// Home della dashboard
// ---------------------------------------------------------------------------

export function getCoda(comune: string | null, signal?: AbortSignal): Promise<CodaLavoro> {
  return apiGet<CodaLavoro>('/redazione/home/coda/', {
    ...AUTH,
    signal,
    query: { comune: comune ?? undefined },
  });
}

export function getNumeri(
  comune: string | null,
  giorni = 30,
  signal?: AbortSignal,
): Promise<NumeriHome> {
  return apiGet<NumeriHome>('/redazione/home/numeri/', {
    ...AUTH,
    signal,
    query: { comune: comune ?? undefined, giorni },
  });
}

export function getAttivitaRecente(
  comune: string | null,
  limite = 30,
  signal?: AbortSignal,
): Promise<readonly VoceAudit[]> {
  return apiGet<readonly VoceAudit[]>('/redazione/home/attivita/', {
    ...AUTH,
    signal,
    query: { comune: comune ?? undefined, limite },
  });
}

/** `comune` è OBBLIGATORIO: la completezza è per scheda comune. */
export function getCompletezza(comune: string, signal?: AbortSignal): Promise<CompletezzaHome> {
  return apiGet<CompletezzaHome>('/redazione/home/completezza/', {
    ...AUTH,
    signal,
    query: { comune },
  });
}

// ---------------------------------------------------------------------------
// Audit trasversale
// ---------------------------------------------------------------------------

export function getAudit(
  filtri: FiltriLista,
  signal?: AbortSignal,
): Promise<{ meta: MetaLista; risultati: readonly VoceAudit[] }> {
  return apiGet<{ meta: MetaLista; risultati: readonly VoceAudit[] }>('/redazione/audit/', {
    ...AUTH,
    signal,
    query: filtri,
  });
}

// ---------------------------------------------------------------------------
// Notifiche in-app — nessuna email (decisione 79)
// ---------------------------------------------------------------------------

export function getNotifiche(
  soloDaLeggere: boolean,
  signal?: AbortSignal,
): Promise<{ meta: MetaLista; risultati: readonly Notifica[] }> {
  return apiGet<{ meta: MetaLista; risultati: readonly Notifica[] }>('/redazione/notifiche/', {
    ...AUTH,
    signal,
    query: soloDaLeggere ? { solo_da_leggere: 1 } : {},
  });
}

export function segnaLetta(id: number): Promise<Notifica> {
  return apiPost<Notifica>(`/redazione/notifiche/${id}/letta/`, {}, AUTH);
}

export function leggiTutte(): Promise<{ segnate: number }> {
  return apiPost<{ segnate: number }>('/redazione/notifiche/leggi-tutte/', {}, AUTH);
}

export function contaDaLeggere(signal?: AbortSignal): Promise<{ da_leggere: number }> {
  return apiGet<{ da_leggere: number }>('/redazione/notifiche/da-leggere/', {
    ...AUTH,
    signal,
  });
}

// ---------------------------------------------------------------------------
// Viste salvate e preferenze colonne — personali
// ---------------------------------------------------------------------------

export function getVisteSalvate(signal?: AbortSignal): Promise<readonly VistaSalvata[]> {
  return apiGet<readonly VistaSalvata[]>('/redazione/viste-salvate/', { ...AUTH, signal });
}

export function creaVistaSalvata(dati: {
  entita: string;
  nome: string;
  filtri: Readonly<Record<string, unknown>>;
  predefinita?: boolean;
}): Promise<VistaSalvata> {
  return apiPost<VistaSalvata>('/redazione/viste-salvate/', dati, AUTH);
}

export function eliminaVistaSalvata(id: number): Promise<void> {
  return apiDelete(`/redazione/viste-salvate/${id}/`, AUTH);
}

export function getPreferenzeColonne(
  signal?: AbortSignal,
): Promise<readonly PreferenzaColonne[]> {
  return apiGet<readonly PreferenzaColonne[]>('/redazione/preferenze-colonne/', {
    ...AUTH,
    signal,
  });
}

export function salvaPreferenzaColonne(
  entita: string,
  dati: { colonne: readonly string[]; ordinamento: string; per_pagina: number },
): Promise<PreferenzaColonne> {
  return apiPut<PreferenzaColonne>(`/redazione/preferenze-colonne/${entita}/`, dati, AUTH);
}

// ---------------------------------------------------------------------------
// Libreria media (decisione 80)
// ---------------------------------------------------------------------------

export function getMedia(filtri: FiltriLista, signal?: AbortSignal): Promise<ListaMedia> {
  return apiGet<ListaMedia>('/redazione/media/', { ...AUTH, signal, query: filtri });
}

export function getMediaCestino(
  filtri: FiltriLista,
  signal?: AbortSignal,
): Promise<ListaMedia> {
  return apiGet<ListaMedia>('/redazione/media/cestino/', { ...AUTH, signal, query: filtri });
}

export function caricaMedia(
  file: File,
  metadati: Readonly<Record<string, string>>,
): Promise<Media> {
  const form = new FormData();
  form.append('file', file);
  for (const [chiave, valore] of Object.entries(metadati)) {
    if (valore !== '') form.append(chiave, valore);
  }
  return apiPost<Media>('/redazione/media/', form, AUTH);
}

/** Metadati in JSON: solo l'upload e la sostituzione sono multipart. */
export function aggiornaMedia(
  id: number,
  dati: Readonly<Record<string, unknown>>,
): Promise<Media> {
  return apiPatchConEtag<Media>(`/redazione/media/${id}/`, dati, AUTH).then((r) => r.dati);
}

/** Sostituisce il file MANTENENDO l'URL: cambia solo `?v=<versione>`. */
export function sostituisciMedia(id: number, file: File): Promise<Media> {
  const form = new FormData();
  form.append('file', file);
  return apiPost<Media>(`/redazione/media/${id}/sostituisci/`, form, AUTH);
}

export function ritagliaMedia(
  id: number,
  ritaglio: { x: number; y: number; larghezza: number; altezza: number },
): Promise<Media> {
  return apiPost<Media>(`/redazione/media/${id}/ritaglia/`, ritaglio, AUTH);
}

export function eliminaMedia(id: number): Promise<void> {
  return apiDelete(`/redazione/media/${id}/`, AUTH);
}

export function ripristinaMedia(id: number): Promise<Media> {
  return apiPost<Media>(`/redazione/media/${id}/ripristina/`, {}, AUTH);
}

/** Cancella riga E file. Solo admin. */
export function eliminaMediaDefinitivo(id: number): Promise<void> {
  return apiDelete(`/redazione/media/${id}/definitivo/`, AUTH);
}

// ---------------------------------------------------------------------------
// Import CSV in due fasi (decisione 78)
// ---------------------------------------------------------------------------

/**
 * Fase 1: valida riga per riga e **non scrive nulla**. Le colonne
 * sconosciute sono un errore della riga, non vengono ignorate.
 */
export function caricaCsv(dati: {
  file: File;
  entita: string;
  comune?: string;
  separatore?: string;
}): Promise<ImportazioneCsv> {
  const form = new FormData();
  form.append('file', dati.file);
  form.append('entita', dati.entita);
  if (dati.comune) form.append('comune', dati.comune);
  if (dati.separatore) form.append('separatore', dati.separatore);
  return apiPost<ImportazioneCsv>('/redazione/importazioni-csv/', form, AUTH);
}

/** Fase 2: scrive le sole righe che la fase 1 ha dichiarato valide. */
export function confermaCsv(chiave: string): Promise<ImportazioneCsv> {
  return apiPost<ImportazioneCsv>(`/redazione/importazioni-csv/${chiave}/conferma/`, {}, AUTH);
}

export function annullaCsv(chiave: string): Promise<ImportazioneCsv> {
  return apiPost<ImportazioneCsv>(`/redazione/importazioni-csv/${chiave}/annulla/`, {}, AUTH);
}

export function getImportazioniCsv(
  signal?: AbortSignal,
): Promise<{ meta: MetaLista; risultati: readonly ImportazioneCsv[] }> {
  return apiGet<{ meta: MetaLista; risultati: readonly ImportazioneCsv[] }>(
    '/redazione/importazioni-csv/',
    { ...AUTH, signal },
  );
}

// ---------------------------------------------------------------------------
// Importatori da file legacy
// ---------------------------------------------------------------------------

export function getSorgentiImport(
  signal?: AbortSignal,
): Promise<{ sorgenti: readonly SorgenteImport[] }> {
  return apiGet<{ sorgenti: readonly SorgenteImport[] }>('/redazione/importatori/sorgenti/', {
    ...AUTH,
    signal,
  });
}

export function getEsecuzioniImport(
  signal?: AbortSignal,
): Promise<{ meta: MetaLista; risultati: readonly EsecuzioneImport[] }> {
  return apiGet<{ meta: MetaLista; risultati: readonly EsecuzioneImport[] }>(
    '/redazione/importatori/',
    { ...AUTH, signal },
  );
}

/** SINCRONO: la richiesta resta aperta per tutta la durata dell'import. */
export function eseguiImport(sorgente: string, dryRun: boolean): Promise<EsecuzioneImport> {
  return apiPost<EsecuzioneImport>(
    '/redazione/importatori/esegui/',
    { sorgente, dry_run: dryRun },
    AUTH,
  );
}

// ---------------------------------------------------------------------------
// Import remoto dei punti di interesse (MiC via Cruscotto Italia)
// ---------------------------------------------------------------------------

/** Il codice dell'unico importatore remoto configurabile, per ora. */
export const IMPORTATORE_POI = 'poi_cruscotto';

export function getConfigurazioneImportatore(
  codice: string,
  signal?: AbortSignal,
): Promise<ConfigurazioneImportatore> {
  return apiGet<ConfigurazioneImportatore>(
    `/redazione/importatori/configurazioni/${codice}/`,
    { ...AUTH, signal },
  );
}

export function salvaConfigurazioneImportatore(
  codice: string,
  modifiche: Partial<ConfigurazioneImportatore>,
): Promise<ConfigurazioneImportatore> {
  return apiPut<ConfigurazioneImportatore>(
    `/redazione/importatori/configurazioni/${codice}/`,
    modifiche,
    AUTH,
  );
}

/**
 * SINCRONO: una GET verso l'endpoint remoto e il confronto con il portale.
 * Non scrive niente — è quello che rende l'anteprima selezionabile senza
 * rischi.
 */
export function anteprimaImportPoi(
  comune: string,
  sottofonti?: readonly string[],
): Promise<AnteprimaImportPoi> {
  return apiPost<AnteprimaImportPoi>(
    '/redazione/punti-interesse/import/anteprima/',
    { comune, ...(sottofonti ? { sottofonti } : {}) },
    AUTH,
  );
}

/** SINCRONO. `chiavi` è la selezione fatta in anteprima: mai implicita. */
export function applicaImportPoi(
  comune: string,
  chiavi: readonly string[],
  opzioni: { readonly dryRun: boolean; readonly sottofonti?: readonly string[] },
): Promise<EsitoImportPoi> {
  return apiPost<EsitoImportPoi>(
    '/redazione/punti-interesse/import/applica/',
    {
      comune,
      chiavi,
      dry_run: opzioni.dryRun,
      ...(opzioni.sottofonti ? { sottofonti: opzioni.sottofonti } : {}),
    },
    AUTH,
  );
}

/** SINCRONO e lungo: una chiamata HTTP per comune. Solo amministratori. */
export function batchImportPoi(opzioni: {
  readonly dryRun: boolean;
  readonly sottofonti?: readonly string[];
  readonly maxComuni?: number;
}): Promise<EsitoBatchImportPoi> {
  return apiPost<EsitoBatchImportPoi>(
    '/redazione/punti-interesse/import/batch/',
    {
      dry_run: opzioni.dryRun,
      ...(opzioni.sottofonti ? { sottofonti: opzioni.sottofonti } : {}),
      ...(opzioni.maxComuni ? { max_comuni: opzioni.maxComuni } : {}),
    },
    AUTH,
  );
}

export function impostaCampiBloccati(
  id: number,
  campi: readonly string[],
): Promise<{ id: number; campi_bloccati: readonly string[] }> {
  return apiPost<{ id: number; campi_bloccati: readonly string[] }>(
    `/redazione/punti-interesse/${id}/campi-bloccati/`,
    { campi },
    AUTH,
  );
}

// ---------------------------------------------------------------------------
// Geocodifica (decisione 83)
// ---------------------------------------------------------------------------

/**
 * Geocodifica SERVER-SIDE, e non lato client come suggerisce il contratto.
 *
 * Il motivo è la CSP del portale (`docker/nginx/intestazioni-sicurezza.conf`):
 * `connect-src 'self' https://api.open-meteo.com`. Una chiamata del browser a
 * Nominatim sarebbe bloccata dal browser stesso — la strada «da preferire»
 * del contratto qui non è percorribile senza allargare la CSP, e allargare la
 * CSP per una comodità dell'editor è un prezzo che paga il sito pubblico.
 *
 * Se `IC_GEOCODER_URL` è vuoto il server risponde 503
 * `geocodifica_non_configurata`: non è un guasto, è una funzione non
 * configurata, e la UI lo dice offrendo l'inserimento manuale.
 */
/**
 * Le categorie di itinerario per le PREFERENZE del configuratore.
 *
 * Si legge l'endpoint PUBBLICO `/itinerari/categorie/` e non la risorsa
 * `categorie-itinerario` del registro: quella è `ruolo_minimo: admin`, e il
 * wizard è dei revisori — la ricerca redazionale risponderebbe loro 403
 * proprio mentre il generatore le loro categorie preferite le accetta.
 * L'endpoint pubblico elenca anche le categorie a zero itinerari, che è
 * esattamente l'insieme su cui `categorie_preferite` viene validato.
 */
export interface CategoriaItinerarioScelta {
  readonly slug: string;
  readonly nome: string;
  readonly icona?: string;
}

export async function getCategorieItinerarioPerScelta(
  signal?: AbortSignal,
): Promise<readonly CategoriaItinerarioScelta[]> {
  const dati = await apiGet<unknown>('/itinerari/categorie/', signal ? { signal } : {});
  if (Array.isArray(dati)) return dati as readonly CategoriaItinerarioScelta[];
  // Tolleranza alla forma impaginata, come nel client pubblico: lo schema
  // dichiara l'array nudo, ma una busta non deve svuotare l'elenco.
  if (dati && typeof dati === 'object' && 'risultati' in dati) {
    return (dati as { risultati: readonly CategoriaItinerarioScelta[] }).risultati;
  }
  return [];
}

export function geocodifica(
  indirizzo: string,
  comune?: string,
): Promise<RispostaGeocodifica> {
  return apiPost<RispostaGeocodifica>(
    '/redazione/geocodifica/',
    { indirizzo, ...(comune ? { comune } : {}) },
    AUTH,
  );
}

// ---------------------------------------------------------------------------
// Utenti e ambiti (decisione 84)
// ---------------------------------------------------------------------------

export function getUtentiDashboard(signal?: AbortSignal): Promise<readonly UtenteDashboard[]> {
  return apiGet<readonly UtenteDashboard[]>('/redazione/utenti/', { ...AUTH, signal });
}

export function creaUtenteDashboard(dati: {
  email: string;
  nome_visualizzato: string;
  ruolo: string;
  password: string;
  comuni?: readonly string[];
}): Promise<UtenteDashboard> {
  return apiPost<UtenteDashboard>('/redazione/utenti/', dati, AUTH);
}

export function aggiornaUtenteDashboard(
  id: number,
  dati: Readonly<Record<string, unknown>>,
): Promise<UtenteDashboard> {
  return apiPatchConEtag<UtenteDashboard>(`/redazione/utenti/${id}/`, dati, AUTH).then(
    (r) => r.dati,
  );
}

export function eliminaUtenteDashboard(id: number): Promise<void> {
  return apiDelete(`/redazione/utenti/${id}/`, AUTH);
}

// ---------------------------------------------------------------------------
// Navigazione e home pubbliche (decisione 82) — lettura, senza autenticazione
// ---------------------------------------------------------------------------

export function getMenuPubblico(
  parametri: { comune?: string; ambito?: string; lang?: string } = {},
  signal?: AbortSignal,
): Promise<RispostaNavigazione<VoceMenuPubblica>> {
  return apiGet<RispostaNavigazione<VoceMenuPubblica>>('/menu/', { signal, query: parametri });
}

export function getHomePubblica(
  parametri: { comune?: string; lang?: string } = {},
  signal?: AbortSignal,
): Promise<RispostaNavigazione<FasciaHome>> {
  return apiGet<RispostaNavigazione<FasciaHome>>('/home/', { signal, query: parametri });
}

// ---------------------------------------------------------------------------
// Metadati dei campi — la lacuna del catalogo, colmata
// ---------------------------------------------------------------------------

/**
 * Il catalogo dichiara i NOMI dei campi, non i loro tipi né le loro scelte.
 * Senza tipi, l'editor generico sarebbe un muro di caselle di testo, e un
 * campo con enum produrrebbe 400 a ogni salvataggio.
 *
 * `OPTIONS` sul viewset è la strada: DRF serve `actions.POST` con tipo,
 * obbligatorietà, etichetta e `choices` di ogni campo. Non è documentato nel
 * contratto — è comportamento standard di DRF — quindi è trattato come un
 * **arricchimento facoltativo**: se non risponde, l'editor ripiega sulle
 * euristiche di `campiDaEuristica`. Mai un errore mostrato all'utente per un
 * OPTIONS che non c'è.
 */
interface CampoOptions {
  readonly type?: string;
  readonly required?: boolean;
  readonly read_only?: boolean;
  readonly label?: string;
  readonly help_text?: string;
  readonly max_length?: number;
  readonly choices?: readonly { value: unknown; display_name?: string }[];
}

const TIPI_OPTIONS: Readonly<Record<string, DescrizioneCampo['tipo']>> = {
  string: 'testo',
  integer: 'intero',
  decimal: 'numero',
  float: 'numero',
  boolean: 'booleano',
  date: 'data',
  datetime: 'data_ora',
  choice: 'scelta',
  'multiple choice': 'scelta_multipla',
  field: 'json',
  email: 'email',
  url: 'url',
  slug: 'testo',
  nested_object: 'json',
  list: 'json',
};

/**
 * `OPTIONS /redazione/{entita}/`. Il fallimento non è un errore da
 * propagare: è un arricchimento che non c'è, e il chiamante ripiega sulle
 * euristiche senza mostrare niente all'utente.
 */
export async function optionsRisorsa(
  entita: string,
  signal?: AbortSignal,
): Promise<readonly DescrizioneCampo[] | null> {
  try {
    const corpo = await apiOptions<{
      actions?: { POST?: Record<string, CampoOptions>; PUT?: Record<string, CampoOptions> };
    }>(`/redazione/${entita}/`, { ...AUTH, ...(signal ? { signal } : {}) });
    const campi = corpo?.actions?.POST ?? corpo?.actions?.PUT;
    if (!campi) return null;
    return Object.entries(campi).map(([nome, meta]) => descriviDaOptions(nome, meta));
  } catch {
    return null;
  }
}

function descriviDaOptions(nome: string, meta: CampoOptions): DescrizioneCampo {
  const tipoGrezzo = (meta.type ?? 'string').toLowerCase();
  let tipo: DescrizioneCampo['tipo'] = TIPI_OPTIONS[tipoGrezzo] ?? 'testo';
  if (tipo === 'testo' && (meta.max_length ?? 0) > 300) tipo = 'testo_lungo';
  if (nome === 'blocchi') tipo = 'blocchi';
  return {
    nome,
    etichetta: meta.label ?? etichettaDaNome(nome),
    tipo,
    obbligatorio: meta.required === true,
    sola_lettura: meta.read_only === true,
    ...(meta.choices
      ? {
          scelte: meta.choices.map((s) => ({
            valore: String(s.value),
            etichetta: s.display_name ?? String(s.value),
          })),
        }
      : {}),
    ...(meta.help_text ? { aiuto: meta.help_text } : {}),
    ...(meta.max_length ? { max_lunghezza: meta.max_length } : {}),
  };
}

// ---------------------------------------------------------------------------
// Catalogo esteso: `campi_dettagliati` (decisione 110)
// ---------------------------------------------------------------------------

/**
 * Come il tipo dichiarato dal backend diventa un widget.
 *
 * La tabella è volutamente noiosa e in un posto solo: è il contratto fra due
 * enum, e disperderla nei componenti significherebbe che un tipo nuovo si
 * scopre da un `switch` che cade nel `default`.
 */
const WIDGET_PER_TIPO: Readonly<Record<string, TipoWidget>> = {
  stringa: 'testo',
  testo: 'testo_lungo',
  testo_ricco: 'testo_ricco',
  intero: 'intero',
  decimale: 'numero',
  booleano: 'booleano',
  data: 'data',
  datetime: 'data_ora',
  scelta: 'scelta',
  // Il riferimento si decide DOPO, in `descriviDaCatalogo`: con `scelte` in
  // linea è un menu a tendina, con `origine_scelte` è una ricerca. Mapparlo
  // qui su `scelta` produceva un `<select>` vuoto sui 30 campi che l'elenco
  // in linea non ce l'hanno — fra cui `comune`, che è obbligatorio.
  riferimento: 'riferimento',
  lista_stringhe: 'lista_stringhe',
  struttura: 'struttura',
  blocchi: 'blocchi',
  media: 'media',
  url: 'url',
  coordinate: 'coordinata',
  tecnico: 'tecnico',
};

/** Normalizza le due forme di `scelte`: la nostra e quella di DRF. */
function normalizzaScelte(
  scelte: readonly Readonly<Record<string, unknown>>[] | null | undefined,
): readonly { valore: string; etichetta: string; numerica: boolean }[] | undefined {
  if (!Array.isArray(scelte) || scelte.length === 0) return undefined;
  return scelte.map((voce) => {
    const grezzo = voce.valore ?? voce.value;
    const valore = String(grezzo ?? '');
    const etichetta = String(voce.etichetta ?? voce.display_name ?? valore);
    // Le pk arrivano come numeri: il DOM le renderà stringhe, e al ritorno
    // vanno riconvertite (vedi `scelte_numeriche`).
    return { valore, etichetta, numerica: typeof grezzo === 'number' };
  });
}

/**
 * Un descrittore del catalogo → la descrizione con cui si costruisce il campo.
 *
 * Un `tipo` che questo client non conosce **non diventa in silenzio una
 * casella di testo**: diventa una casella di testo che DICHIARA di non aver
 * riconosciuto il tipo (`tipoIgnoto`), così chi scrive sa perché il campo
 * sembra povero e chi sviluppa sa cosa aggiungere.
 */
export function descriviDaCatalogo(descrittore: DescrittoreCampo): DescrizioneCampo {
  const widget = WIDGET_PER_TIPO[descrittore.tipo];
  const tecnico = descrittore.tipo === 'tecnico';
  const scelte = normalizzaScelte(descrittore.scelte);
  /*
   * Come si decide il widget di un riferimento:
   *
   *   scelte in linea  → menu a tendina (semplice o multiplo)
   *   solo origine     → ricerca incrementale
   *
   * L'ordine è questo perché un elenco già in mano si sfoglia più in fretta
   * di quanto si scriva; sopra le 60 righe il backend smette di mandarlo ed
   * è lì che serve la ricerca.
   */
  let tipo: TipoWidget | undefined = widget;
  if (widget === 'riferimento') {
    tipo = scelte ? 'scelta' : 'riferimento';
  }
  // `multiplo` su una scelta è la scelta multipla: senza, si salverebbe una
  // stringa dove il server aspetta una lista.
  if (tipo === 'scelta' && descrittore.multiplo === true) tipo = 'scelta_multipla';
  const schemaStruttura = traduciSchemaStruttura(descrittore.schema);
  return {
    nome: descrittore.nome,
    etichetta: descrittore.etichetta || etichettaDaNome(descrittore.nome),
    tipo: tipo ?? 'testo',
    obbligatorio: descrittore.obbligatorio === true,
    // «`tecnico` implica `sola_lettura: true`»: lo si impone qui invece di
    // fidarsi, perché un campo tecnico modificabile per svista sarebbe un
    // payload d'audit riscrivibile a mano.
    sola_lettura: descrittore.sola_lettura === true || tecnico,
    ...(widget ? {} : { tipoIgnoto: descrittore.tipo }),
    ...(scelte ? { scelte } : {}),
    ...(scelte && scelte.some((s) => s.numerica) ? { scelte_numeriche: true } : {}),
    ...(descrittore.aiuto ? { aiuto: descrittore.aiuto } : {}),
    ...(descrittore.max_lunghezza ? { max_lunghezza: descrittore.max_lunghezza } : {}),
    ...(descrittore.vocabolario ? { vocabolario: descrittore.vocabolario } : {}),
    ...(descrittore.traducibile === true ? { traducibile: true } : {}),
    ...(typeof descrittore.lista === 'boolean' ? { lista: descrittore.lista } : {}),
    ...(descrittore.multiplo === true ? { multiplo: true } : {}),
    ...(descrittore.origine_scelte ? { origine_scelte: descrittore.origine_scelte } : {}),
    ...(schemaStruttura ? { schema: schemaStruttura.campi } : {}),
    // La forma A OGGETTO dello schema DICE se il valore è una lista
    // (`lista_oggetti` sì, `oggetto` no) e vince sul flag `lista` del
    // descrittore, che su quelle strutture non arriva. La forma ad array non
    // lo sa e non decide: resta il flag, poi il valore presente.
    ...(schemaStruttura && schemaStruttura.lista !== undefined
      ? { lista: schemaStruttura.lista }
      : {}),
  };
}

/**
 * Traduce lo `schema` di una `struttura` nei descrittori del form.
 *
 * Il backend lo scrive in DUE forme (vedi `SchemaStruttura`): l'array piatto
 * di campi e l'oggetto `{tipo: "oggetto"|"lista_oggetti", …}` con cui
 * arrivano `orari_apertura` e la configurazione delle fasce home. Fino
 * all'11/08/2026 questo client riconosceva solo l'array: le strutture a
 * oggetto perdevano lo schema in silenzio e degradavano alla via di fuga
 * JSON — «Campo strutturato di cui il catalogo non dichiara la forma» detto
 * su un campo la cui forma il catalogo dichiarava eccome.
 *
 * `null` = nessuno schema utilizzabile: il chiamante non inventa un modulo.
 */
function traduciSchemaStruttura(
  schema: DescrittoreCampo['schema'],
): { readonly campi: readonly DescrizioneCampo[]; readonly lista?: boolean } | null {
  if (!schema) return null;
  if (Array.isArray(schema)) {
    if (schema.length === 0) return null;
    // La forma storica non dice se è lista: si lascia decidere al chiamante
    // (flag `lista` del descrittore, poi il valore presente).
    return { campi: schema.map(descriviDaCatalogo) };
  }
  const oggetto = schema as SchemaStruttura;
  if (oggetto.tipo === 'oggetto' && Array.isArray(oggetto.campi)) {
    return { campi: oggetto.campi.map(descriviDaCatalogo), lista: false };
  }
  if (oggetto.tipo === 'lista_oggetti') {
    const elemento = oggetto.elemento;
    if (elemento && elemento.tipo === 'oggetto' && Array.isArray(elemento.campi)) {
      return { campi: elemento.campi.map(descriviDaCatalogo), lista: true };
    }
  }
  // Una forma che questo client non conosce: meglio la via di fuga JSON di
  // un editor costruito su uno schema capito a metà.
  return null;
}

// ---------------------------------------------------------------------------
// Riferimenti con ricerca incrementale (decisione 123)
// ---------------------------------------------------------------------------

/** Una riga trovata: la chiave da salvare e l'etichetta da mostrare. */
export interface RigaRiferimento {
  readonly valore: string | number;
  readonly etichetta: string;
}

/**
 * Estrae chiave ed etichetta da una riga, seguendo `origine_scelte`.
 *
 * Si manda `risultato[campo_valore]` e basta: dal 04/08/2026 il descrittore
 * dice la verità (si deduce dal campo DRF, non dal lookup di URL). Il ripiego
 * che deduceva la forma della chiave dal valore già salvato è stato tolto —
 * non serviva più e sui record NUOVI non poteva funzionare, perché non c'era
 * nessun valore da cui dedurre.
 *
 * Il TIPO della chiave si conserva com'è: `id` è un numero, `slug` una
 * stringa, e convertirli avrebbe rimesso in piedi lo stesso problema.
 */
function rigaRiferimento(
  riga: Readonly<Record<string, unknown>>,
  origine: OrigineScelte,
): RigaRiferimento | null {
  const chiave = riga[origine.campo_valore];
  if (typeof chiave !== 'string' && typeof chiave !== 'number') return null;
  const grezza = riga[origine.campo_etichetta];
  const etichetta =
    typeof grezza === 'string' && grezza.trim() !== '' ? grezza : String(chiave);
  return { valore: chiave, etichetta };
}

/** L'endpoint dichiarato è assoluto (`/api/v2/…`); il client ha già la base. */
function percorsoRelativo(endpoint: string): string {
  const base = (import.meta.env.VITE_API_BASE ?? '/api/v2').replace(/[/]$/, '');
  return endpoint.startsWith(base) ? endpoint.slice(base.length) : endpoint;
}

/**
 * `GET {endpoint}?{parametro_ricerca}=<testo>`.
 *
 * Il `signal` non è facoltativo per comodità: è quello che permette a chi
 * chiama di buttare via una risposta superata invece di lasciarla arrivare
 * fuori ordine.
 */
export async function cercaRiferimenti(
  origine: OrigineScelte,
  testo: string,
  signal?: AbortSignal,
  limite = 20,
): Promise<readonly RigaRiferimento[]> {
  const risposta = await apiGet<ListaRisorsa>(percorsoRelativo(origine.endpoint), {
    ...AUTH,
    ...(signal ? { signal } : {}),
    query: {
      [origine.parametro_ricerca]: testo || undefined,
      per_pagina: limite,
    },
  });
  return (risposta.risultati ?? []).flatMap((riga) => {
    const estratta = rigaRiferimento(riga, origine);
    return estratta ? [estratta] : [];
  });
}

/**
 * Le etichette di PIÙ chiavi già salvate, con UNA richiesta.
 *
 * `GET {endpoint}?{parametro_chiave}=52,7,19`. Sostituisce tre ripieghi che
 * esistevano perché il catalogo non bastava: il dettaglio per chiave, la
 * ricerca con corrispondenza esatta e — la peggiore — lo scaricamento di
 * cinquecento righe da tenere in cache per tradurre un numero in un nome.
 *
 * Vincoli dichiarati dal server: tetto di **500 chiavi** (oltre, 400
 * `validazione`) e una chiave non numerica su un filtro numerico è **400**,
 * non un elenco vuoto. Per questo l'errore non viene inghiottito: chi chiama
 * mostra le chiavi non risolte dichiarandolo, invece di far sparire i valori.
 *
 * Restituisce solo quello che ha trovato: una chiave che non torna indietro è
 * una riga che non esiste più, ed è un'informazione, non un guasto.
 */
export async function risolviRiferimenti(
  origine: OrigineScelte,
  chiavi: readonly (string | number)[],
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, string>> {
  const risolte = new Map<string, string>();
  const uniche = Array.from(new Set(chiavi.map(String))).filter((c) => c !== '');
  if (uniche.length === 0 || !origine.parametro_chiave) return risolte;
  const risposta = await apiGet<ListaRisorsa>(percorsoRelativo(origine.endpoint), {
    ...AUTH,
    ...(signal ? { signal } : {}),
    query: {
      [origine.parametro_chiave]: uniche.join(','),
      per_pagina: uniche.length,
    },
  });
  for (const riga of risposta.risultati ?? []) {
    const estratta = rigaRiferimento(riga, origine);
    if (estratta) risolte.set(String(estratta.valore), estratta.etichetta);
  }
  return risolte;
}

// ---------------------------------------------------------------------------
// Vocabolari aperti (decisione 111)
// ---------------------------------------------------------------------------

/**
 * `GET /redazione/vocabolari/<chiave>/`.
 *
 * La forma dell'involucro non è fissata dal contratto (dice solo «→ voci»):
 * si accettano `{voci}`, `{risultati}` e l'array nudo. Sbagliare
 * l'involucro qui vorrebbe dire un autocompletamento sempre vuoto senza che
 * nessuno se ne accorga, ed è esattamente il tipo di silenzio che questo
 * progetto non ammette.
 */
export async function getVocabolario(
  chiave: string,
  parametri: { q?: string; limite?: number } = {},
  signal?: AbortSignal,
): Promise<Vocabolario> {
  return apiGet<Vocabolario>(`/redazione/vocabolari/${encodeURIComponent(chiave)}/`, {
    ...AUTH,
    ...(signal ? { signal } : {}),
    query: { q: parametri.q || undefined, limite: parametri.limite ?? 50 },
  });
}

/**
 * L'indice delle chiavi.
 *
 * Prima si ricavava incrociando i `campi_dettagliati` di tutte le risorse, e
 * la chiave di un campo non ancora a catalogo non compariva. Ora è un
 * endpoint (lacuna n. 4, chiusa).
 */
export function getIndiceVocabolari(
  signal?: AbortSignal,
): Promise<{ vocabolari: readonly VoceIndiceVocabolari[] }> {
  return apiGet<{ vocabolari: readonly VoceIndiceVocabolari[] }>('/redazione/vocabolari/', {
    ...AUTH,
    ...(signal ? { signal } : {}),
  });
}

/** Solo admin: promuove un valore a voce ufficiale del vocabolario. */
export function promuoviVoceVocabolario(
  chiave: string,
  valore: string,
  extra: { etichetta?: string; note?: string; ordine?: number } = {},
): Promise<VoceVocabolario> {
  return apiPost<VoceVocabolario>(
    `/redazione/vocabolari/${encodeURIComponent(chiave)}/promuovi/`,
    { valore, etichetta: extra.etichetta ?? '', note: extra.note ?? '', ordine: extra.ordine ?? 0 },
    AUTH,
  );
}

/**
 * Solo admin: unifica più valori in uno, **riscrivendo i contenuti**.
 *
 * `prova: true` esegue lo stesso codice e fa rientrare la transazione: il
 * `contenuti_toccati` che torna è il numero **vero**, non una stima. È la
 * ragione per cui la conferma non conta più gli `usi` a mano — una stima
 * calcolata con una seconda logica diverge dalla prima al primo caso limite,
 * per esempio un contenuto che ha già il valore di destinazione.
 */
export function unificaVocabolario(
  chiave: string,
  valori: readonly string[],
  valoreFinale: string,
  opzioni: { prova?: boolean; etichetta?: string } = {},
): Promise<EsitoUnificazione> {
  return apiPost<EsitoUnificazione>(
    `/redazione/vocabolari/${encodeURIComponent(chiave)}/unifica/`,
    {
      valori,
      valore_finale: valoreFinale,
      etichetta: opzioni.etichetta ?? '',
      prova: opzioni.prova === true,
    },
    AUTH,
  );
}

/** «descrizione_breve» → «Descrizione breve». */
export function etichettaDaNome(nome: string): string {
  const parole = nome.replace(/_/g, ' ').trim();
  return parole.charAt(0).toUpperCase() + parole.slice(1);
}

/**
 * Il ripiego di ultima istanza, quando NÉ `campi_dettagliati` NÉ `OPTIONS`
 * rispondono.
 *
 * Dal 04/08/2026 `campi_dettagliati` è su tutte le voci del catalogo, quindi
 * questa strada non si percorre più in condizioni normali: resta perché una
 * dashboard che non sa costruire un modulo quando il catalogo non risponde è
 * una dashboard che non si apre. L'euristica che deduceva la SOLA LETTURA dal
 * nome del campo è stata tolta — quella la dichiara il backend, provata campo
 * per campo, e indovinarla significava rendere non modificabile un campo che
 * il serializer accetta.
 */
/**
 * Campi che sono liste di etichette libere, e da quale vocabolario pescano.
 *
 * È l'euristica dichiarata della decisione 112: si applica **solo ai nomi
 * elencati qui**, mai per somiglianza. Vale finché il backend non manda
 * `campi_dettagliati`, che vince sempre.
 */
const VOCABOLARI_PER_NOME: Readonly<Record<string, string>> = {
  tag: 'comune.tag',
  cucine: 'attivita.cucine',
  servizi: 'attivita.servizi',
  accessibilita: 'attivita.accessibilita',
  alimentazione: 'attivita.alimentazione',
  lingue: 'attivita.lingue',
  highlights: 'comune.highlights',
  servizi_dichiarati: 'attivita.servizi_dichiarati',
  servizi_disabili: 'attivita.servizi_disabili',
  settori: 'comune.settori',
};

export function campiDaEuristica(
  risorsa: RisorsaCatalogo,
  valori: Readonly<Record<string, unknown>> = {},
): readonly DescrizioneCampo[] {
  const soloLettura = new Set([
    'id',
    'creato_il',
    'aggiornato_il',
    'stato_redazionale',
    'pubblicato_il',
    'pubblicato_da',
    'comune_slug',
    'etichetta',
    'stati_raggiungibili',
    'traduzioni',
    'completezza_traduzioni',
  ]);
  return (risorsa.campi ?? []).map((nome) => {
    const valore = valori[nome];
    const tipo = tipoDaNome(nome, valore);
    const vocabolario = VOCABOLARI_PER_NOME[nome];
    return {
      nome,
      etichetta: etichettaDaNome(nome),
      tipo,
      obbligatorio: false,
      sola_lettura: soloLettura.has(nome),
      ...(tipo === 'lista_stringhe' && vocabolario ? { vocabolario } : {}),
    };
  });
}

function tipoDaNome(nome: string, valore: unknown): DescrizioneCampo['tipo'] {
  if (nome === 'blocchi') return 'blocchi';
  // Le liste di etichette vengono prima della regola sui nomi narrativi, che
  // altrimenti manderebbe `highlights` in `testo_lungo`.
  if (nome in VOCABOLARI_PER_NOME && (valore === null || valore === undefined || Array.isArray(valore))) {
    return 'lista_stringhe';
  }
  if (nome === 'latitudine' || nome === 'longitudine') return 'coordinata';
  if (/^(centro_lat|centro_lon)$/.test(nome)) return 'coordinata';
  if (/(^|_)url$/.test(nome) || nome.endsWith('_url')) return 'url';
  if (nome.includes('email')) return 'email';
  if (/_il$/.test(nome)) return 'data_ora';
  if (/^data_|_data$|scadenza|inizio|fine|decorrenza/.test(nome)) return 'data';
  if (typeof valore === 'boolean') return 'booleano';
  if (typeof valore === 'number') return 'numero';
  if (Array.isArray(valore) || (valore !== null && typeof valore === 'object')) return 'json';
  if (/testo|descrizione|note|orari|messaggio|sommario|highlights|estratto/.test(nome)) {
    return 'testo_lungo';
  }
  return 'testo';
}
